import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FileText, Download, Eye, ShoppingCart, Calendar, Search, 
  Filter, X, AlertCircle, CheckCircle, Loader, ChevronLeft, 
  ChevronRight, Star, Users, Lock, Phone, Mail, LogIn
} from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../Header';
import Footer from '../Footer';

const PremiumDocs = () => {
  const navigate = useNavigate();
  
  // Component State
  const [pdfs, setPdfs] = useState([]);
  const [filteredPdfs, setFilteredPdfs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [priceFilter, setPriceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [buyingPdfId, setBuyingPdfId] = useState(null);
  const [purchaseMessage, setPurchaseMessage] = useState(null);
  const [purchasedDocuments, setPurchasedDocuments] = useState([]);
  const [customAccessDocuments, setCustomAccessDocuments] = useState([]);
  const [userData, setUserData] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // ==================== START: LOGIN PROTECTION LOGIC ====================
  const [checkingLogin, setCheckingLogin] = useState(true);

  // ==================== NEW: Free Access Tracking ====================
  const [freeAccessDocuments, setFreeAccessDocuments] = useState({});

  // Page metadata========================================================
  const [pageMetadata, setPageMetadata] = useState({
    page_names: 'Loading...',
    description: 'Please wait while we load content...'
  });
  const [metadataLoading, setMetadataLoading] = useState(true);

  const fetchPageMetadata = async () => {
    try {
      setMetadataLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/page-metadata/public/page11`);
      console.log('Metadata API Response:', response.data);
      
      if (response.data) {
        setPageMetadata({
          page_names: response.data.page_name || 'Premium Docs', 
          description: response.data.description || 'Premium Document Update'
        });
      }
    } catch (error) {
      console.error('Error fetching page metadata:', error);
      setPageMetadata({
        page_names: 'Premium Docs',
        description: 'Premium Document Update'
      });
    } finally {
      setMetadataLoading(false);
    }
  };

  // Fetch page metadata immediately when component mounts
  useEffect(() => {
    fetchPageMetadata();
  }, []);

  // Metadata End===========================================================

  // ==================== NEW: Check free access for a document ====================
  const checkFreeAccess = async (documentId, userId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/pdf/check-free-access/${documentId}/${userId}`);
      
      if (response.data.success && response.data.hasFreeAccess) {
        return {
          hasFreeAccess: true,
          matchedColumns: response.data.matchedColumns || []
        };
      }
      return {
        hasFreeAccess: false,
        matchedColumns: []
      };
    } catch (error) {
      console.error('Error checking free access:', error);
      return {
        hasFreeAccess: false,
        matchedColumns: []
      };
    }
  };

  // ==================== NEW: Check custom access for user from doc_custom_access ====================
  const fetchCustomAccessDocuments = async (userId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/pdf/custom-access/user/${userId}`);
      
      if (response.data && response.data.success) {
        return response.data.data.map(item => item.doc_id);
      }
      return [];
    } catch (error) {
      console.error('Error fetching custom access documents:', error);
      return [];
    }
  };

  // ==================== Check if user has access (purchased OR custom access OR free access) ====================
  const hasDocumentAccess = (documentId) => {
    const isPurchased = purchasedDocuments.some(purchase => purchase.document_id === documentId);
    if (isPurchased) return true;
    
    const hasCustomAccess = customAccessDocuments.includes(documentId);
    if (hasCustomAccess) return true;
    
    const freeAccess = freeAccessDocuments[documentId];
    if (freeAccess && freeAccess.hasFreeAccess) return true;
    
    return false;
  };

  // ==================== Get access type for display ====================
  const getAccessType = (documentId) => {
    if (purchasedDocuments.some(purchase => purchase.document_id === documentId)) {
      return 'PURCHASED';
    }
    if (customAccessDocuments.includes(documentId)) {
      return 'CUSTOM ACCESS';
    }
    const freeAccess = freeAccessDocuments[documentId];
    if (freeAccess && freeAccess.hasFreeAccess) {
      return 'FREE ACCESS';
    }
    return null;
  };

  // ==================== Check free access for all documents ====================
  const checkAllDocumentsFreeAccess = async (userId, documents) => {
    if (!userId || !documents.length) return;
    
    const accessMap = {};
    for (const doc of documents) {
      const access = await checkFreeAccess(doc.id, userId);
      accessMap[doc.id] = access;
    }
    setFreeAccessDocuments(accessMap);
  };

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Check login status
  const checkLoginStatus = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const userDataStr = localStorage.getItem('user');
      
      if (!accessToken || !userDataStr) {
        setIsLoggedIn(false);
        setUserData(null);
        setAuthChecked(true);
        setCheckingLogin(false);
        return;
      }

      const user = JSON.parse(userDataStr);
      setIsLoggedIn(true);
      setUserData(user);
      
      if (user.id) {
        await fetchPurchasedDocuments(user.id);
        const customAccessIds = await fetchCustomAccessDocuments(user.id);
        setCustomAccessDocuments(customAccessIds);
      }
      
      setAuthChecked(true);
      setCheckingLogin(false);
    } catch (error) {
      console.error('Error checking login status:', error);
      setIsLoggedIn(false);
      setUserData(null);
      setAuthChecked(true);
      setCheckingLogin(false);
    }
  };

  // Fetch purchased documents for current user
  const fetchPurchasedDocuments = async (userId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/pdf/purchased/${userId}`);
      
      if (response.data.success) {
        setPurchasedDocuments(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching purchased documents:', error);
    }
  };

  // Fetch all PDFs from API - ALWAYS fetch regardless of login status
  const fetchPDFs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/pdf`);
      
      console.log('PDF API Response:', response.data);
      
      if (response.data) {
        setPdfs(response.data);
        setFilteredPdfs(response.data);
        
        // Check free access for all documents if user is logged in
        if (userData?.id && response.data.length) {
          await checkAllDocumentsFreeAccess(userData.id, response.data);
        }
      }
    } catch (err) {
      console.error('Error fetching PDFs:', err);
      setError('Failed to load premium documents. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Apply filters and search
  useEffect(() => {
    let filtered = [...pdfs];
    
    if (searchTerm) {
      filtered = filtered.filter(pdf => 
        pdf.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pdf.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (priceFilter === 'free') {
      filtered = filtered.filter(pdf => !pdf.price || pdf.price === 0 || parseFloat(pdf.price) === 0);
    } else if (priceFilter === 'paid') {
      filtered = filtered.filter(pdf => pdf.price && parseFloat(pdf.price) > 0);
    }
    
    if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (sortBy === 'price_low') {
      filtered.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
    } else if (sortBy === 'price_high') {
      filtered.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
    }
    
    setFilteredPdfs(filtered);
    setCurrentPage(1);
  }, [searchTerm, priceFilter, sortBy, pdfs]);

  // Initialize - check login and fetch PDFs
  useEffect(() => {
    const init = async () => {
      await checkLoginStatus();
      // ALWAYS fetch PDFs regardless of login status
      await fetchPDFs();
    };
    init();
    
    const handleStorageChange = () => {
      checkLoginStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Re-check free access and custom access when user data changes
  useEffect(() => {
    if (userData?.id && pdfs.length) {
      checkAllDocumentsFreeAccess(userData.id, pdfs);
      const refreshCustomAccess = async () => {
        const customAccessIds = await fetchCustomAccessDocuments(userData.id);
        setCustomAccessDocuments(customAccessIds);
      };
      refreshCustomAccess();
    }
  }, [userData, pdfs]);

  // Format price - show "FREE" for zero price
  const formatPrice = (price) => {
    const numericPrice = parseFloat(price);
    if (isNaN(numericPrice) || numericPrice === 0) return 'FREE';
    return `₹${numericPrice.toFixed(2)}`;
  };

  // Get numeric price value
  const getNumericPrice = (price) => {
    const numericPrice = parseFloat(price);
    return isNaN(numericPrice) ? 0 : numericPrice;
  };

  // Check if document is free (price is 0 or null or "0" or "0.00")
  const isFreeDocument = (price) => {
    if (!price && price !== 0) return true;
    const numericPrice = parseFloat(price);
    return isNaN(numericPrice) || numericPrice === 0;
  };

  // ==================== Handle Razorpay Payment ====================
  const handleBuyNow = async (pdf) => {
    if (!isLoggedIn) {
      setPurchaseMessage({
        type: 'error',
        text: 'Please login to purchase premium documents'
      });
      setTimeout(() => setPurchaseMessage(null), 3000);
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      return;
    }

    if (isFreeDocument(pdf.price)) {
      handleDownloadPDF(pdf);
      return;
    }

    if (hasDocumentAccess(pdf.id)) {
      setPurchaseMessage({
        type: 'success',
        text: 'You already have access to this document! You can view/download it now.'
      });
      setTimeout(() => setPurchaseMessage(null), 3000);
      return;
    }

    setBuyingPdfId(pdf.id);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const orderResponse = await axios.post(`${apiUrl}/api/pdf/create-order`, {
        document_id: pdf.id,
        amount: getNumericPrice(pdf.price),
        title: pdf.title,
        userId: userData?.id 
      });

      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.message || 'Failed to create order');
      }

      const { order, document } = orderResponse.data;
      const isScriptLoaded = await loadRazorpayScript();
      if (!isScriptLoaded) {
        throw new Error('Failed to load Razorpay SDK');
      }

      const options = {
        key: 'rzp_live_RS3BFvuEreQaHx',
        amount: order.amount,
        currency: order.currency,
        name: 'Market Maestroo',
        description: `Purchase: ${document.title}`,
        image: '/logo.png',
        order_id: order.id,
        handler: async (response) => {
          try {
            const verifyResponse = await axios.post(`${apiUrl}/api/pdf/verify-payment`, {
              order_id: order.id,
              payment_id: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              document_id: document.id,
              document_title: document.title,
              amount: getNumericPrice(document.price),
              userId: userData?.id 
            });

            if (verifyResponse.data.success) {
              setPurchaseMessage({
                type: 'success',
                text: `Successfully purchased "${document.title}"! You can now view and download.`
              });
              if (userData?.id) {
                await fetchPurchasedDocuments(userData.id);
              }
              setBuyingPdfId(null);
              setTimeout(() => setPurchaseMessage(null), 3000);
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            setPurchaseMessage({
              type: 'error',
              text: 'Payment verification failed. Please contact support.'
            });
            setBuyingPdfId(null);
            setTimeout(() => setPurchaseMessage(null), 3000);
          }
        },
        prefill: {
          name: userData?.username || '',
          email: userData?.email || '',
          contact: userData?.mobile || ''
        },
        theme: {
          color: '#3b82f6'
        },
        modal: {
          ondismiss: () => {
            setBuyingPdfId(null);
            setPurchaseMessage({
              type: 'error',
              text: 'Payment cancelled'
            });
            setTimeout(() => setPurchaseMessage(null), 3000);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Payment initiation error:', error);
      setPurchaseMessage({
        type: 'error',
        text: error.message || 'Failed to initiate payment. Please try again.'
      });
      setBuyingPdfId(null);
      setTimeout(() => setPurchaseMessage(null), 3000);
    }
  };

  // ==================== Handle View PDF ====================
  const handleViewPDF = (pdf) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
    
    if (!hasDocumentAccess(pdf.id) && !isFreeDocument(pdf.price)) {
      setPurchaseMessage({
        type: 'error',
        text: 'Please purchase this document or check your access permissions to view'
      });
      setTimeout(() => setPurchaseMessage(null), 3000);
      return;
    }
    
    window.open(`${apiUrl}/${pdf.pdf}`, '_blank');
  };

  // ==================== Handle Download PDF ====================
  const handleDownloadPDF = (pdf) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
    
    if (!hasDocumentAccess(pdf.id) && !isFreeDocument(pdf.price)) {
      setPurchaseMessage({
        type: 'error',
        text: 'Please purchase this document or check your access permissions to download'
      });
      setTimeout(() => setPurchaseMessage(null), 3000);
      return;
    }
    
    window.open(`${apiUrl}/${pdf.pdf}`, '_blank');
  };

  // Open preview modal
  const handlePreview = (pdf) => {
    setSelectedPdf(pdf);
    setShowPreviewModal(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPdfs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPdfs.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setPriceFilter('all');
    setSortBy('newest');
  };

  // ==================== LOGIN PROTECTION RENDER LOGIC ====================
  
  // Show loading state while checking authentication
  if (checkingLogin) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
          <div className="text-center">
            <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Checking authentication...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Show login required screen if user is not logged in
  if (!isLoggedIn) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="text-3xl text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
                <p className="text-gray-600 mb-1">You need to log in to access Premium Documents.</p>
                <p className="text-sm text-gray-500 mb-6">This content is exclusive to registered members.</p>
                <Link to="/login">
                  <button className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3">
                    <LogIn className="text-lg" />
                    Go to Login Page
                  </button>
                </Link>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Don't have an account?{' '}
                    <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                      Sign up here
                    </Link>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="opacity-40 pointer-events-none">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <h1 className="text-3xl md:text-4xl font-bold mb-4">
                  {pageMetadata.page_names || 'Premium Documents'}
                </h1>
                <p className="text-blue-100 text-lg">
                  {pageMetadata.description || 'Premium Document Update'}
                </p>
              </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="text-center py-20">
                <p className="text-gray-400">Content blurred for privacy. Please login to view.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // ==================== MAIN RENDER LOGIC (Only for authenticated users) ====================
  
  // Show loading state while fetching data
  if (isLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
          <div className="text-center">
            <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading premium documents...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!authChecked) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
          <div className="text-center">
            <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">

        {/* Page Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {metadataLoading ? (
              <div className="flex items-center gap-3">
                <Loader className="w-6 h-6 text-white animate-spin" />
                <p className="text-blue-100">Loading page information...</p>
              </div>
            ) : (
              <>
                <h1 className="text-3xl md:text-4xl font-bold mb-4">
                  {pageMetadata.page_names}
                </h1>
                <p className="text-blue-100 text-lg">
                  {pageMetadata.description}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filters Section */}
          <div className="bg-white rounded-xl shadow-md p-4 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search documents by title or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                  value={priceFilter}
                  onChange={(e) => setPriceFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Documents</option>
                  <option value="free">Free Only</option>
                  <option value="paid">Premium Only</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                </select>
              </div>
              
              {(searchTerm || priceFilter !== 'all' || sortBy !== 'newest') && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Clear Filters
                </button>
              )}
            </div>
            
            <div className="mt-4 text-sm text-gray-600">
              Found {filteredPdfs.length} document{filteredPdfs.length !== 1 ? 's' : ''}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {purchaseMessage && (
            <div className={`mb-4 p-4 rounded-lg flex items-center gap-3 ${
              purchaseMessage.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {purchaseMessage.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <p>{purchaseMessage.text}</p>
            </div>
          )}

          {!isLoading && !error && (
            <>
              {filteredPdfs.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl shadow-md">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
                  <p className="text-gray-500">Try adjusting your search or filter criteria</p>
                  <button
                    onClick={clearFilters}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentItems.map((pdf) => {
                      const hasAccess = hasDocumentAccess(pdf.id);
                      const accessType = getAccessType(pdf.id);
                      const freeAccessInfo = freeAccessDocuments[pdf.id];
                      const isPurchased = purchasedDocuments.some(purchase => purchase.document_id === pdf.id);
                      const hasCustom = customAccessDocuments.includes(pdf.id);
                      const needsPurchase = !hasAccess && !isFreeDocument(pdf.price);
                      const isFree = isFreeDocument(pdf.price);
                      const displayPrice = formatPrice(pdf.price);
                      
                      return (
                        <div
                          key={pdf.id}
                          className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col"
                        >
                          {pdf.poster ? (
                            <div className="relative h-80 bg-gray-100">
                              <img
                                src={`${process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in'}/${pdf.poster}`}
                                alt={pdf.title}
                                className="w-full h-full object-cover"
                              />
                              {hasAccess && (
                                <div className={`absolute top-3 left-3 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1 ${
                                  isPurchased ? 'bg-green-500' : (hasCustom ? 'bg-purple-500' : 'bg-blue-500')
                                }`}>
                                  {isPurchased ? (
                                    <CheckCircle className="w-3 h-3" />
                                  ) : hasCustom ? (
                                    <Users className="w-3 h-3" />
                                  ) : (
                                    <Star className="w-3 h-3" />
                                  )}
                                  {accessType}
                                </div>
                              )}
                              {!hasAccess && !isFree && (
                                <div className="absolute top-3 left-3 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                                  <Star className="w-3 h-3" />
                                  PREMIUM
                                </div>
                              )}
                              {freeAccessInfo && freeAccessInfo.hasFreeAccess && !isPurchased && !hasCustom && (
                                <div className="absolute top-3 right-3 bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                                  Free Access
                                </div>
                              )}
                              {isFree && !hasAccess && (
                                <div className="absolute top-3 left-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  FREE
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="h-48 bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                              <FileText className="w-16 h-16 text-blue-400" />
                            </div>
                          )}
                          
                          <div className="p-5 flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-1 text-sm text-gray-500">
                                <Calendar className="w-4 h-4" />
                                <span>{formatDate(pdf.created_at)}</span>
                              </div>
                              <div className={`flex items-center gap-1 font-bold ${
                                isFree ? 'text-green-600' : 'text-blue-600'
                              }`}>
                                <span>{displayPrice}</span>
                              </div>
                            </div>
                            
                            <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">
                              {pdf.title}
                            </h3>
                            <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                              {pdf.content}
                            </p>
                            
                            {/* Button Section - Conditional Rendering */}
                            <div className="flex gap-2 mt-auto">
                              {needsPurchase ? (
                                <button
                                  onClick={() => handleBuyNow(pdf)}
                                  disabled={buyingPdfId === pdf.id}
                                  className="w-full px-3 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {buyingPdfId === pdf.id ? (
                                    <Loader className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <ShoppingCart className="w-4 h-4" />
                                  )}
                                  Buy Now
                                </button>
                              ) : hasAccess ? (
                                <>
                                  <button
                                    onClick={() => handleViewPDF(pdf)}
                                    className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition flex items-center justify-center gap-2 text-sm font-medium"
                                  >
                                    <Eye className="w-4 h-4" />
                                    View
                                  </button>
                                  <button
                                    onClick={() => handleDownloadPDF(pdf)}
                                    className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition flex items-center justify-center gap-2 text-sm font-medium"
                                  >
                                    <Download className="w-4 h-4" />
                                    Download
                                  </button>
                                </>
                              ) : isFree ? (
                                <button
                                  onClick={() => handleDownloadPDF(pdf)}
                                  className="w-full px-3 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg transition flex items-center justify-center gap-2 text-sm font-medium"
                                >
                                  <Download className="w-4 h-4" />
                                  Free Download
                                </button>
                              ) : null}
                            </div>
                            
                            {/* Show Preview button for all documents */}
                            <div className="mt-2">
                              <button
                                onClick={() => handlePreview(pdf)}
                                className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition flex items-center justify-center gap-2 text-sm"
                              >
                                <Eye className="w-4 h-4" />
                                Preview
                              </button>
                            </div>
                            
                            {/* Show access info tooltip */}
                            {hasCustom && !isPurchased && (
                              <div className="mt-2 text-xs text-purple-600 text-center">
                                Custom access granted by admin
                              </div>
                            )}
                            {freeAccessInfo && freeAccessInfo.hasFreeAccess && !isPurchased && !hasCustom && (
                              <div className="mt-2 text-xs text-purple-600 text-center">
                                Access via {freeAccessInfo.matchedColumns?.join(', ') || 'permissions'}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-8">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => paginate(pageNum)}
                            className={`w-10 h-10 rounded-lg transition ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
        
        {/* Updated Preview Modal to Display Actual Document */}
        {showPreviewModal && selectedPdf && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000] p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl max-w-5xl w-full h-[90vh] overflow-hidden flex flex-col shadow-2xl">
              <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                <div>
                  <h3 className="font-bold text-xl text-gray-800">Document Preview</h3>
                  <p className="text-xs text-gray-500">{selectedPdf.title}</p>
                </div>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="p-2 hover:bg-red-50 hover:text-red-600 rounded-full transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="flex-1 bg-gray-100 relative overflow-hidden">
                <iframe
                  src={`${process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in'}/${selectedPdf.pdf}#toolbar=0`}
                  className="w-full h-full border-none"
                  title="PDF Preview"
                />
              </div>
              
              <div className="p-4 border-t bg-gray-50 flex flex-col sm:flex-row gap-3 items-center justify-between">
                <div className="text-sm text-gray-600 font-medium">
                  {isFreeDocument(selectedPdf.price) ? 'Free Resource - No Purchase Needed' : `Premium Access: ${formatPrice(selectedPdf.price)}`}
                </div>
                <div className="flex gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => setShowPreviewModal(false)}
                    className="flex-1 sm:px-6 py-2 border border-gray-300 rounded-xl hover:bg-gray-100 transition font-medium"
                  >
                    Close
                  </button>
                  {hasDocumentAccess(selectedPdf.id) || isFreeDocument(selectedPdf.price) ? (
                    <button
                      onClick={() => {
                        setShowPreviewModal(false);
                        handleDownloadPDF(selectedPdf);
                      }}
                      className="flex-1 sm:px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition flex items-center justify-center gap-2 font-bold"
                    >
                      <Download className="w-4 h-4" />
                      Download 
                    </button>
                  ) : !isFreeDocument(selectedPdf.price) ? (
                    <button
                      onClick={() => {
                        setShowPreviewModal(false);
                        handleBuyNow(selectedPdf);
                      }}
                      disabled={buyingPdfId === selectedPdf.id}
                      className="flex-1 sm:px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition flex items-center justify-center gap-2 disabled:opacity-50 font-bold"
                    >
                      {buyingPdfId === selectedPdf.id ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <ShoppingCart className="w-4 h-4" />
                      )}
                      Buy Access
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
      <Footer />
    </>
  );
};

export default PremiumDocs;